import { useState, useRef, useEffect, useCallback } from "react";
import { colors } from "../theme";

type SwipeState = "default" | "swiping" | "triggered" | "processing" | "success" | "fail";

interface SwipeTrackProps {
  label: string;
  onComplete: () => void;
  processing?: boolean;
  success?: boolean;
  error?: string | null;
}

// Detect non-touch desktop environments so we can render a plain tap
// button instead of the swipe track, which was confusing / invisible on
// Telegram Desktop (VEN-23).
const isTouchDevice = typeof window !== "undefined" && (
  "ontouchstart" in window ||
  (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0)
);

export default function SwipeTrack({ label, onComplete, processing, success, error }: SwipeTrackProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<SwipeState>("default");
  const [pos, setPos] = useState(0);
  const startX = useRef(0);
  const trackWidth = useRef(0);

  // Desktop (no touch) gets a regular button. Same onComplete, same label,
  // same visual states — just tap-to-confirm instead of swipe-to-confirm.
  if (!isTouchDevice) {
    const isBusy = processing || success || state === "processing" || state === "success";
    const showError = !!error;
    return (
      <button
        type="button"
        onClick={() => {
          if (isBusy) return;
          onComplete();
        }}
        disabled={isBusy}
        style={{
          width: "100%",
          height: 56,
          flexShrink: 0,
          border: "none",
          borderRadius: 14,
          background: showError ? "rgba(239,68,68,0.15)" : colors.primary,
          color: showError ? colors.danger : colors.bg,
          fontSize: 15,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          cursor: isBusy ? "default" : "pointer",
          opacity: isBusy ? 0.7 : 1,
        }}
      >
        {success || state === "success"
          ? "\u2713 Done!"
          : processing || state === "processing"
            ? "\u23F3 Confirming..."
            : showError
              ? error
              : label}
      </button>
    );
  }

  // Sync external processing/success/error to internal state
  useEffect(() => {
    if (success) setState("success");
    else if (error) setState("fail");
    else if (processing) setState("processing");
    else if (state === "triggered" || state === "processing") setState("default");
  }, [processing, success, error]);

  const onStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (state === "triggered" || state === "processing" || state === "success") return;
    if (state === "fail") setState("default"); // allow retry after error
    setState("swiping");
    startX.current = "touches" in e ? e.touches[0].clientX : e.clientX;
    trackWidth.current = trackRef.current?.offsetWidth || 300;
  }, [state]);

  useEffect(() => {
    if (state !== "swiping") return;

    const onMove = (e: TouchEvent | MouseEvent) => {
      const cx = "touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const maxSlide = trackWidth.current - 56;
      const pct = Math.max(0, Math.min((cx - startX.current) / maxSlide, 1));
      setPos(pct);

      if (pct >= 0.78) {
        setState("triggered");
        // Haptic feedback if available
        try { (window as any).Telegram?.WebApp?.HapticFeedback?.impactOccurred("heavy"); } catch {}
        setTimeout(() => onComplete(), 500);
      }
    };

    const onEnd = () => {
      if (state === "swiping") {
        setPos(0);
        setState("default");
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [state, onComplete]);

  const handleX = pos * (trackWidth.current - 56);
  const isActive = state === "swiping" || state === "default";
  const isDone = state === "triggered" || state === "processing" || state === "success";

  return (
    <div
      ref={trackRef}
      style={{
        position: "relative",
        width: "100%",
        height: 56,
        flexShrink: 0,
        borderRadius: 14,
        background: colors.surfaceLight,
        overflow: "hidden",
        userSelect: "none",
        touchAction: "none",
      }}
    >
      {/* Green fill behind handle */}
      {isActive && pos > 0 && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: handleX + 56,
            background: `linear-gradient(90deg, rgba(173,255,0,0.15), rgba(173,255,0,0.08))`,
            borderRadius: 14,
            transition: state === "swiping" ? "none" : "width 0.3s ease",
          }}
        />
      )}

      {/* Handle */}
      {isActive && (
        <div
          onTouchStart={onStart}
          onMouseDown={onStart}
          style={{
            position: "absolute",
            left: handleX,
            top: 4,
            width: 48,
            height: 48,
            borderRadius: 12,
            background: colors.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            fontWeight: 800,
            color: colors.bg,
            cursor: "grab",
            transition: state === "swiping" ? "none" : "left 0.3s ease",
            boxShadow: pos > 0.5 ? `0 0 20px rgba(173,255,0,0.4)` : `0 0 12px rgba(173,255,0,0.19)`,
          }}
        >
          {"\u226B"}
        </div>
      )}

      {/* Label — right-aligned, fades as you swipe */}
      {isActive && (
        <div
          style={{
            position: "absolute",
            right: 20,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 14,
            fontWeight: 700,
            color: colors.grayLight,
            opacity: Math.max(0, 1 - pos * 2),
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </div>
      )}

      {/* Triggered / Processing / Success / Fail states */}
      {isDone && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            fontWeight: 700,
            color: colors.primary,
            background: `rgba(173,255,0,0.06)`,
            borderRadius: 14,
          }}
        >
          {state === "triggered" && "\u2713 Opening wallet..."}
          {state === "processing" && "\u23F3 Confirming..."}
          {state === "success" && "\u2713 Done!"}
        </div>
      )}

      {state === "fail" && (
        <div
          onTouchStart={onStart}
          onMouseDown={onStart}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            fontWeight: 700,
            color: colors.danger,
            cursor: "pointer",
          }}
        >
          {error || "Failed — swipe to retry"}
        </div>
      )}
    </div>
  );
}
